// 3rd party imports
import * as React from 'react';
import { inject } from 'mobx-react';

// bernie
import { ContextStore } from 'bernie-core';

// ui
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading, UitkSubheading, UitkText } from 'uitk-react-text';
import { UitkList, UitkListItem } from 'uitk-react-list';
import { UitkLink } from 'uitk-react-link';

// mobx
import { Legal } from 'src/models';
import { LegalStore } from '../../stores';

export interface PrivacyPolicyProps {
  context?: ContextStore;
  legal?: LegalStore;
}

export interface PrivacyPolicyState {
  legal?: Legal;
}

@inject('context', 'legal')
export class PrivacyPolicy extends React.Component<PrivacyPolicyProps, PrivacyPolicyState> {
  constructor(props) {
    super(props);

    const siteId = this.props.context.site.id;
    this.state = {
      legal: this.props.legal.getConfig(siteId),
    };
  }

  public render() {
    const { email } = this.state.legal;

    return (
      <UitkSpacing className="container--small" padding={{ block: 'twelve' }}>
        <main>
          <UitkSubheading>Last updated 2nd June 2021</UitkSubheading>
          <UitkText spacing="six">
            Affiliate Hub, part of the{' '}
            <UitkLink inline={true}>
              <a href="https://expediagroup.com/travel-with-us/default.aspx" target="_blank">
                Expedia Group
              </a>
            </UitkLink>
            , ('we' or 'us') values you as our customer and recognises that privacy is important to you. This Privacy
            Statement explains how we collect, use and disclose data when you use our platform and associated services,
            and tells you how to contact us.
          </UitkText>

          {/* section 1 */}
          <UitkSpacing margin={{ blockstart: 'six' }}>
            <div>
              <UitkHeading tag="h2" size={3}>
                Privacy Statement summary
              </UitkHeading>
              <UitkText spacing="six">
                This is a summary of our Privacy Statement. To review our Privacy Statement in full, please scroll down.
              </UitkText>
              <UitkSpacing margin={{ blockstart: 'six' }}>
                <UitkHeading tag="h3" size={5}>
                  What does this Privacy Statement cover?
                </UitkHeading>
              </UitkSpacing>
              <UitkText spacing="two">This Privacy Statement is designed to describe:</UitkText>
              <UitkSpacing className="uitk-type-list-400" padding={{ block: 'three', inline: 'four' }}>
                <UitkList>
                  <UitkListItem>
                    What type of personal information we collect and use, and how we collect it
                  </UitkListItem>
                  <UitkListItem>When and with whom we share your personal information</UitkListItem>
                  <UitkListItem>
                    What choices you can make about how we collect, use and share your personal information
                  </UitkListItem>
                </UitkList>
              </UitkSpacing>
              <UitkSpacing margin={{ blockstart: 'six' }}>
                <UitkHeading tag="h3" size={5}>
                  What personal information do we collect and use, and how do we collect it?
                </UitkHeading>
              </UitkSpacing>
              <UitkText spacing="two">We collect personal information when:</UitkText>
              <UitkSpacing className="uitk-type-list-400" padding={{ block: 'three', inline: 'four' }}>
                <UitkList>
                  <UitkListItem>You give us the information</UitkListItem>
                  <UitkListItem>We collect it automatically</UitkListItem>
                  <UitkListItem>We receive it from others </UitkListItem>
                </UitkList>
              </UitkSpacing>
              <UitkText spacing="two">
                When you create an account on one of our sites, sign up to receive offers or information or make a
                booking using our platform, you give us your personal information. We also collect such information
                through automated technology such as cookies placed on your browser, with your consent where applicable,
                when you visit our sites or download and use our Apps. We also receive information from affiliated
                companies within Expedia Group, as well as business partners and other third parties, which help us to
                improve our platform and associated tools and services, update and maintain accurate records,
                potentially detect and investigate fraud and more effectively market our services.
              </UitkText>
              <UitkSpacing margin={{ blockstart: 'six' }}>
                <UitkHeading tag="h3" size={5}>
                  How is your personal information shared?
                </UitkHeading>
              </UitkSpacing>
              <UitkText spacing="two">
                Your personal information may be shared to help you to book your travel and/or holiday, assist with your
                travel and/or holiday stay, communicate with you (including when we send information on products and
                services or enable you to communicate with travel providers and/or property owners) and comply with the
                law. The full Privacy Statement details how personal information is shared.
              </UitkText>
              <UitkSpacing margin={{ blockstart: 'six' }}>
                <UitkHeading tag="h3" size={5}>
                  What are your rights and choices?
                </UitkHeading>
              </UitkSpacing>
              <UitkText spacing="two">
                You can exercise your data protection rights in various ways. For example, you can opt out of marketing
                by clicking the 'unsubscribe' link in the emails, in your account, where applicable, or by contacting
                our customer service. Our Privacy Statement below has more information about the options and data
                protection rights available to you.
              </UitkText>
              <UitkSpacing margin={{ blockstart: 'six' }}>
                <UitkHeading tag="h3" size={5}>
                  How to contact us
                </UitkHeading>
              </UitkSpacing>
              <UitkText spacing="two">
                More information about our privacy practices is in our full Privacy Statement below. You can also
                contact us as described below in the 'Contact us' section to ask questions about how we handle your
                personal information or make requests regarding your personal information.
              </UitkText>
            </div>
          </UitkSpacing>

          {/* Section 2 */}
          <UitkSpacing margin={{ blockstart: 'six' }}>
            <div>
              <UitkHeading tag="h2" size={3}>
                Privacy Statement
              </UitkHeading>

              <UitkSpacing margin={{ blockstart: 'six' }}>
                <UitkHeading tag="h3" size={5}>
                  Categories of personal information that we collect
                </UitkHeading>
              </UitkSpacing>
              <UitkText spacing="two">
                When you use our platform, apps or associated tools or services, we may collect the following kinds of
                personal information from you as needed:
              </UitkText>
              <UitkSpacing className="uitk-type-list-400" padding={{ block: 'three', inline: 'four' }}>
                <UitkList>
                  <UitkListItem>
                    Name, email address, telephone number and home, business and billing addresses
                  </UitkListItem>
                  <UitkListItem>
                    Government-issued identification required for booking, or identity verification such as passport,
                    driving licence and government redress numbers, and for holiday property owners, their tax
                    identification number
                  </UitkListItem>
                  <UitkListItem>
                    Payment information such as payment card number, expiry date and billing address
                  </UitkListItem>
                  <UitkListItem>
                    Travel-related preferences and requests such as favourite destination and accommodation types, and
                    special dietary and accessibility needs, as available
                  </UitkListItem>
                  <UitkListItem>Loyalty scheme and membership information</UitkListItem>
                  <UitkListItem>Birth date and gender</UitkListItem>
                  <UitkListItem>Geolocation</UitkListItem>
                  <UitkListItem>Images, videos and other recordings</UitkListItem>
                  <UitkListItem>Social media account ID and other publicly available information</UitkListItem>
                  <UitkListItem>
                    Communications with us (such as recordings of calls with customer service representatives for
                    quality assurance and training purposes)
                  </UitkListItem>
                  <UitkListItem>
                    Searches that you conduct, transactions and other interactions with you on our online services and
                    apps
                  </UitkListItem>
                  <UitkListItem>
                    Other communications that occur through the platform among partners and holidaymakers, and in-group
                    chat and collaboration tools for holidaymakers
                  </UitkListItem>
                  <UitkListItem>The searches and transactions that are conducted through the platform</UitkListItem>
                  <UitkListItem>
                    Data that you give us about other people, such as your travel companions or others for whom you are
                    making a booking
                  </UitkListItem>
                  <UitkListItem>
                    Information that we receive about you from other Expedia Group companies and third parties, such as
                    our business and affiliate partners and authorised service providers, which may include updated
                    contact information, demographic information, interests and purchase history, and which we may add
                    to your account or profile and use for market research and analysis
                  </UitkListItem>
                </UitkList>
              </UitkSpacing>
              <UitkText spacing="two">
                When you install any of our apps or use our platform, we automatically collect the following types of
                information from your device:
              </UitkText>
              <UitkSpacing className="uitk-type-list-400" padding={{ block: 'three', inline: 'four' }}>
                <UitkList>
                  <UitkListItem>IP address</UitkListItem>
                  <UitkListItem>Device Type</UitkListItem>
                  <UitkListItem>Unique device identification numbers</UitkListItem>
                  <UitkListItem>
                    Internet browser type (such as Firefox, Safari, Chrome and Internet Explorer)
                  </UitkListItem>
                  <UitkListItem>Internet Service Provider</UitkListItem>
                  <UitkListItem>Operating system</UitkListItem>
                  <UitkListItem>Mobile network</UitkListItem>
                  <UitkListItem>
                    How your device has interacted with our online services, including the pages accessed, links
                    clicked, trips viewed and features used, along with associated dates and times
                  </UitkListItem>
                  <UitkListItem>
                    Details of any referring website or exit pages, as well as general geographic location (such as at
                    the country or city level)
                  </UitkListItem>
                </UitkList>
              </UitkSpacing>
              <UitkText spacing="two">
                Our{' '}
                <UitkLink inline={true}>
                  <a href="cookie-policy">Cookie Statement</a>
                </UitkLink>{' '}
                further explains how we use cookies and similar tracking technology.
              </UitkText>

              <UitkSpacing margin={{ blockstart: 'six' }}>
                <UitkHeading tag="h3" size={5}>
                  Mobile Apps
                </UitkHeading>
              </UitkSpacing>
              <UitkText spacing="two">
                When you download and use any of our mobile apps, we collect certain technical information from your
                device to enable the app to work properly and as otherwise described in this Privacy Statement. That
                technical information includes:
              </UitkText>
              <UitkSpacing className="uitk-type-list-400" padding={{ block: 'three', inline: 'four' }}>
                <UitkList>
                  <UitkListItem>
                    Device and telephone connectivity information such as your carrier, network type, network operator,
                    subscriber identity module ('SIM') operator and SIM country
                  </UitkListItem>
                  <UitkListItem>Operating system and version</UitkListItem>
                  <UitkListItem>Device model</UitkListItem>
                  <UitkListItem>Performance and data usage</UitkListItem>
                  <UitkListItem>
                    Usage data, such as dates and times that the app accesses our servers, the features and links
                    clicked in the app, searches, transactions and the data and files downloaded to the app
                  </UitkListItem>
                  <UitkListItem>
                    Device settings that are selected or enabled, such as Wi-Fi, Global Positioning System ('GPS') and
                    Bluetooth (which may be used for location services, subject to your permission as explained below)
                  </UitkListItem>
                  <UitkListItem>Mobile device settings</UitkListItem>
                  <UitkListItem>
                    Other technical information such as app name, type and version, as needed, to provide you with
                    services
                  </UitkListItem>
                </UitkList>
              </UitkSpacing>
              <UitkHeading tag="h4" size={6}>
                Permissions for location-based services:
              </UitkHeading>
              <UitkText spacing="two">
                Depending on your device’s settings and permissions and your choice to participate in certain programs,
                we may collect the location of your device by using GPS signals, mobile phone towers, Wi-Fi signals,
                Bluetooth or other technologies. We will collect this information, if you opt in through the app or
                other program (either during your initial login or later) to enable certain location-based services that
                are available within the app (for example, locating the closest available accommodation to you). To
                disable location capabilities of the app, you can log off or change your mobile device’s settings.
              </UitkText>

              <UitkSpacing margin={{ blockstart: 'six' }}>
                <UitkHeading tag="h3" size={5}>
                  Use of personal information
                </UitkHeading>
              </UitkSpacing>
              <UitkText spacing="two">
                We use your personal information for various purposes described below, which depend on the site that you
                visit or the app that you use.
              </UitkText>
              <UitkText spacing="two">Your use of online sites, apps and services:</UitkText>
              <UitkSpacing className="uitk-type-list-400" padding={{ block: 'three', inline: 'four' }}>
                <UitkList>
                  <UitkListItem>Book the requested travel or enable booking of a holiday property</UitkListItem>
                  <UitkListItem>Provide services related to the booking and/or account</UitkListItem>
                  <UitkListItem>
                    Create, maintain and update user accounts on our platform and authenticate you as a user
                  </UitkListItem>
                  <UitkListItem>
                    Maintain your search and travel history, accommodation and travel preferences and similar
                    information about your use of Expedia Group’s platform and services, and as otherwise described in
                    this Privacy Statement
                  </UitkListItem>
                  <UitkListItem>
                    Enable and facilitate acceptance and processing of payments, coupons and other transactions
                  </UitkListItem>
                  <UitkListItem>Administer loyalty and rewards schemes</UitkListItem>
                  <UitkListItem>Collect and enable booking-related reviews</UitkListItem>
                  <UitkListItem>
                    Help you to use our services faster and more easily through features, such as the ability to sign in
                    using your account within the online services and sites of some of the Expedia Group companies
                  </UitkListItem>
                </UitkList>
              </UitkSpacing>

              <UitkText spacing="two">Communications and marketing:</UitkText>
              <UitkSpacing className="uitk-type-list-400" padding={{ block: 'three', inline: 'four' }}>
                <UitkList>
                  <UitkListItem>
                    Respond to your questions and requests for information, and process information choices
                  </UitkListItem>
                  <UitkListItem>
                    Enable communication between you and the travel supplier, such as hotels and holiday rental property
                    owners
                  </UitkListItem>
                  <UitkListItem>
                    Contact you (such as by text message, email, phone calls, mail, push notifications or messages on
                    other communication platforms) to provide information such as travel booking confirmations and
                    updates, for marketing purposes or for other purposes as described in this Privacy Statement
                  </UitkListItem>
                  <UitkListItem>
                    Market our products and services, optimise such marketing to be more relevant to you and measure and
                    analyse the effectiveness of our marketing and promotions
                  </UitkListItem>
                  <UitkListItem>Administer promotions such as contests, sweepstakes and similar giveaways</UitkListItem>
                </UitkList>
              </UitkSpacing>

              <UitkText spacing="two">Other business purposes and compliance</UitkText>
              <UitkSpacing className="uitk-type-list-400" padding={{ block: 'three', inline: 'four' }}>
                <UitkList>
                  <UitkListItem>Conduct surveys, market research and data analytics</UitkListItem>
                  <UitkListItem>
                    Maintain, improve, research and measure the effectiveness of our sites and apps, activities, tools
                    and services
                  </UitkListItem>
                  <UitkListItem>
                    Monitor or record calls, chats and other communications with our customer service team and other
                    representatives, as well as platform communications between or among partners and holidaymakers for
                    quality control, training, dispute resolution and as described in this Privacy Statement
                  </UitkListItem>
                  <UitkListItem>
                    Create aggregated or otherwise anonymised or de-identified data, which we may use and disclose
                    without restriction where permissible
                  </UitkListItem>
                  <UitkListItem>
                    Promote security, verify identity of our customers, prevent and investigate fraud and unauthorised
                    activities, defend against claims and other liabilities and manage other risks
                  </UitkListItem>
                  <UitkListItem>
                    Comply with applicable laws, protect our and our users’ rights and interest, defend ourselves and
                    respond to law enforcement, other legal authorities and requests that are part of a legal process
                  </UitkListItem>
                  <UitkListItem>
                    Comply with applicable security and anti-terrorism, anti-bribery, customs and immigration and other
                    such due diligence laws and requirements
                  </UitkListItem>
                  <UitkListItem>
                    Operate our business using lawful business purposes and as permitted by law
                  </UitkListItem>
                </UitkList>
              </UitkSpacing>

              <UitkHeading tag="h4" size={6}>
                Lawful bases for processing:
              </UitkHeading>
              <UitkText spacing="two">
                We will collect personal information from you only (i) where the personal information is necessary to
                perform a contract with you (e.g. manage your booking, process payments or create an account at your
                request), (ii) where the processing is in our legitimate interests and not overridden by your rights (as
                explained below) or (iii) where we have your consent to do so (e.g. sending you marketing communications
                where consent is required). In some cases, we will have a legal obligation to collect personal
                information from you, such as where it is necessary to use your transaction history to complete our
                financial and tax obligations under the law.
              </UitkText>
              <UitkText spacing="two">
                If we ask you to provide personal information to comply with a legal requirement or to perform a
                contract with you, we will make this clear at the relevant time and advise you whether the provision of
                your personal information is mandatory or not (as well as of the possible consequences if you do not
                provide your personal information).
              </UitkText>
              <UitkText spacing="two">
                Certain countries and regions allow us to process personal information on the basis of legitimate
                interests. If we collect and use your personal information in reliance on our legitimate interests (or
                the legitimate interests of any third party), this interest will typically be to operate or improve our
                platform and communicate with you as necessary to provide our services to you, for security verification
                purposes when you contact us, to respond to your queries, undertaking marketing or for the purposes of
                potentially detecting or preventing illegal activities.
              </UitkText>
              <UitkText spacing="two">
                In some cases, we may use automated decision-making, for example, in relation to assessing fraudulent
                transactions or suspicious activity on our site. As part of this processing, automated decisions may be
                made by putting your personal information into a system and calculating the decision using automatic
                processes. If you pose a fraud risk, this may affect your ability to book on our site. You may have
                rights in relation to automated decision making, including the ability to request a manual
                decision-making process instead or to contest a decision based solely on automated processing. If you
                want to know more about your data protection rights, please see the 'Your rights and choices' section
                below.
              </UitkText>

              <UitkSpacing margin={{ blockstart: 'six' }}>
                <UitkHeading tag="h3" size={5}>
                  Sharing of personal information
                </UitkHeading>
              </UitkSpacing>
              <UitkText spacing="two">
                We share your personal information as described below and in this Privacy Statement, and as permitted by
                applicable law.
              </UitkText>
              <UitkSpacing className="uitk-type-list-400" padding={{ block: 'three', inline: 'four' }}>
                <UitkList>
                  <UitkListItem>
                    <strong>Expedia Group companies.</strong> We share your personal information within the Expedia
                    Group companies listed at{' '}
                    <a href="https://expediagroup.com/home/default.aspx" target="_blank">
                      expediagroup.com
                    </a>
                    . Expedia Group companies share, access and use your personal information as described in this
                    Privacy Statement.
                  </UitkListItem>
                  <UitkListItem>
                    <strong>Third-party service providers.</strong> We share personal information with third parties in
                    connection with the delivery of services to you and the operation of our business (for example, to
                    provide credit card processing, customer service, business analytics and fraud prevention and
                    compliance services, and to serve you with advertising that is tailored to your interests). These
                    third-party service providers are required to protect personal information that we share with them,
                    and may not use any directly identifying personal information other than to provide services that we
                    contracted them for. They are not allowed to use the personal information that we share for purposes
                    of their own direct marketing (unless you have separately consented with the third party under the
                    terms provided by the third party).
                  </UitkListItem>
                  <UitkListItem>
                    <strong>Travel suppliers.</strong> We share personal information with travel-related suppliers such
                    as hotels, airlines, car hire companies, insurance, holiday rental property owners and managers and,
                    where available, activity providers, rail companies or cruise lines who fulfil your booking. Please
                    note that travel suppliers may contact you to obtain additional information if and as required to
                    facilitate your booking or to otherwise provide the travel or associated services.
                  </UitkListItem>
                  <UitkListItem>
                    <strong>Business partners and offers.</strong> If we promote a programme or offer a service or
                    product in conjunction with a third-party business partner, we will share your information with that
                    partner to assist in marketing or to provide the associated product or service. In most of those
                    cases, the programme or offer will include the name of the third-party business partner, either
                    alone or with ours, or you will be redirected to the website of that business with notice. An
                    example of such a business partner relationship would be a third-party loyalty scheme for which you
                    could earn points by completing a booking on our platform.
                  </UitkListItem>
                  <UitkListItem>
                    <strong>Other third parties.</strong> When you access certain features, such as Facebook’s 'Like'
                    button or a single sign-on that allows you to log in to our online services with your social media
                    credentials, you will share information with the third party—a social media company, for
                    example—such as the fact that you have visited or interacted with us. In the European Economic Area
                    (EEA), Switzerland and United Kingdom (UK), we will not load social media sharing or sign-on buttons
                    on our website unless and until you accept our use of cookies and similar technologies. For more
                    information, see our{' '}
                    <UitkLink inline={true}>
                      <a href="cookie-policy">Cookie Statement</a>
                    </UitkLink>
                    . The third-party provider may combine this information with other information that they have about
                    you. The personal information shared will be governed by the third-party provider’s privacy policy
                    (including any personal information that we may access via the third-party provider). The
                    third-party providers should inform you about how you can modify your privacy settings on their
                    site.
                  </UitkListItem>
                  <UitkListItem>
                    <strong>Legal rights and obligations.</strong> We may disclose your personal information to enforce
                    our policies, or where we are permitted (or believe in good faith that we are required) to do so by
                    applicable law, such as in response to a request by a law enforcement or governmental authority, in
                    connection with actual or proposed litigation, or to protect and defend our property, people and
                    other rights or interests. We may also share your personal information pursuant to a subpoena or
                    other legal request, or as necessary to remit certain taxes in the course of processing payments as
                    required by law or legal process.
                  </UitkListItem>
                  <UitkListItem>
                    <strong>Corporate transactions.</strong> We may share your personal information in connection with a
                    corporate transaction, such as a divestiture, merger, consolidation, assignments or asset sale, or
                    in the unlikely event of bankruptcy. In the case of any acquisition, we will inform the buyer that
                    it must use your personal information only for the purposes disclosed in this Privacy Statement.
                  </UitkListItem>
                </UitkList>
              </UitkSpacing>

              <UitkSpacing margin={{ blockstart: 'six' }}>
                <UitkHeading tag="h3" size={5}>
                  Your rights and choices
                </UitkHeading>
              </UitkSpacing>
              <UitkText spacing="two">
                You have certain rights and choices with respect to your personal information, as described below:
              </UitkText>
              <UitkSpacing className="uitk-type-list-400" padding={{ block: 'three', inline: 'four' }}>
                <UitkList>
                  <UitkListItem>
                    If you have an account with us, you may change your communication preferences by either (1) logging
                    in and updating the information in your account (not available for all Expedia Group companies) or
                    (2) contacting us <a href={`mailto:${email}`}>here</a>
                  </UitkListItem>
                  <UitkListItem>
                    You can control our use of certain cookies by following the guidance in our{' '}
                    <UitkLink inline={true}>
                      <a href="cookie-policy">Cookie Statement</a>
                    </UitkLink>
                    .
                  </UitkListItem>
                  <UitkListItem>
                    You can update the accuracy of your information at any time by either logging into your account or
                    contacting us
                  </UitkListItem>
                  <UitkListItem>
                    If you no longer wish to receive marketing and promotional emails, you may unsubscribe by clicking
                    the ‘unsubscribe’ link in the email. You can also log in to your account to change communication
                    settings (not available for all Expedia Group companies) or contact us here. Please note that if you
                    choose to unsubscribe from or opt out of marketing emails, we may still send you important
                    transactional and account-related messages from which you will not be able to unsubscribe
                  </UitkListItem>
                  <UitkListItem>
                    For our mobile apps, you can view and manage notifications and preferences in the settings menus of
                    the app and of your operating system
                  </UitkListItem>
                  <UitkListItem>
                    If we are processing your personal information on the basis of consent, you may withdraw that
                    consent at any time by contacting us. Withdrawing your consent will not affect the lawfulness of any
                    processing that occurred before you withdrew consent, and it will not affect our processing of your
                    personal information that is conducted in reliance on a legal basis other than consent
                  </UitkListItem>
                </UitkList>
              </UitkSpacing>
              <UitkText spacing="two">
                Certain countries and regions provide their residents with additional rights relating to personal
                information. These additional rights vary by country and region, and may include the ability to:
              </UitkText>
              <UitkSpacing className="uitk-type-list-400" padding={{ block: 'three', inline: 'four' }}>
                <UitkList>
                  <UitkListItem>Request a copy of your personal information</UitkListItem>
                  <UitkListItem>Request information about the purpose of the processing activities</UitkListItem>
                  <UitkListItem>Delete your personal information</UitkListItem>
                  <UitkListItem>Object to our use or disclosure of your personal information</UitkListItem>
                  <UitkListItem>Restrict the processing of your personal information</UitkListItem>
                  <UitkListItem>Opt out of the sale of your personal information</UitkListItem>
                  <UitkListItem>Port your personal information</UitkListItem>
                  <UitkListItem>
                    Request information about the logic involved in our automated decision-making that is used in our
                    fraud prevention practices, and the result of such decisionsp
                  </UitkListItem>
                </UitkList>
              </UitkSpacing>
              <UitkText spacing="two">
                For more information on what data subject rights may be available to you, please click{' '}
                <a href="https://expediagroup.com/files/doc_downloads/Data-Subject-Rights.pdf" target="_blank">
                  here
                </a>
                .
              </UitkText>
              <UitkText spacing="two">
                In addition to the above rights, you may have the right to complain to a data protection authority about
                our collection and use of your personal information. However, we encourage you to contact us first so
                that we can do our best to resolve your concern. You may submit your request to us using the information
                in the Contact us section. We respond to all requests that we receive from individuals wanting to
                exercise their personal data protection rights in accordance with applicable data protection laws.
              </UitkText>

              <UitkSpacing margin={{ blockstart: 'six' }}>
                <UitkHeading tag="h3" size={5}>
                  International data transfer
                </UitkHeading>
              </UitkSpacing>
              <UitkText spacing="two">
                The personal information that we process may be transmitted or transferred to countries other than the
                country in which you reside. Those countries may have data protection laws that are different from the
                laws of your country.
              </UitkText>
              <UitkText spacing="two">
                The servers for our platform are located in the United States, and the Expedia Group companies and
                third-party service providers operate in many countries around the world. When we collect your personal
                information, we may process it in any of those countries.
              </UitkText>
              <UitkText spacing="two">
                We have taken appropriate steps and put safeguards in place to help ensure that your personal
                information remains protected in accordance with this Privacy Statement. For example, any data transfers
                between our group companies are governed by our intragroup agreements which incorporate strict data
                transfer terms (including the European Commission's Standard Contractual Clauses, for transfers from the
                EEA) and require all group companies to protect the personal information that they process in accordance
                with applicable data protection law. In addition, certain Expedia Group US affiliates have certified
                with the EU-US and Swiss-US Privacy Shield, as explained below in the 'Privacy Shield' section.
              </UitkText>
              <UitkText spacing="two">
                We also require that third-party service providers to whom data transfers are made have appropriate
                safeguards in place to protect your personal information, in compliance with applicable data protection
                law. The particular measures used will depend on the service provider, and our agreements with them may
                include Standard Contractual Clauses approved by the European Commission, the service provider's
                certification under the EU-US and/or Swiss-US Privacy Shield or reliance on the service provider's
                binding corporate rules, as defined by the European Commission.
              </UitkText>

              <UitkSpacing margin={{ blockstart: 'six' }}>
                <UitkHeading tag="h3" size={5}>
                  Privacy Shield
                </UitkHeading>
              </UitkSpacing>
              <UitkText spacing="two">
                Certain Expedia Group US affiliates have certified to the EU-US and Swiss-US Privacy Shield frameworks
                and adhere to the Privacy Shield Principles of Notice, Choice, Accountability for Onward Transfers,
                Security, Data Integrity and Purpose Limitation, Access and Recourse, Enforcement and Liability for
                personal information from the EU, Switzerland and the United Kingdom. Such Expedia Group US affiliates
                will continue to adhere to the Privacy Shield frameworks and Principles even though the CJEU determined
                in July 2020 that the EU-US Privacy Shield framework is no longer an adequate transfer mechanism for the
                transfer of EU personal information to the US. In addition, Expedia Group maintains intra-group standard
                contractual clauses where applicable to cover the transfer of EU personal information to the US. Our
                certifications can be found{' '}
                <a href="https://www.privacyshield.gov/participant?id=a2zt0000000PJU6AAO&status=Active" target="_blank">
                  here
                </a>
                . For more information about the Privacy Shield principles, please visit:{' '}
                <a href="www.privacyshield.gov" target="_blank">
                  www.privacyshield.gov
                </a>
                . For more on our adherence to the Privacy Shield Frameworks, please see information posted{' '}
                <a
                  href="https://expediagroup.com/files/doc_downloads/Expedia-Group-Privacy-Shield-Statement.pdf"
                  target="_blank"
                >
                  here
                </a>
                .
              </UitkText>

              <UitkSpacing margin={{ blockstart: 'six' }}>
                <UitkHeading tag="h3" size={5}>
                  Security
                </UitkHeading>
              </UitkSpacing>
              <UitkText spacing="two">
                We want you to feel confident about using our platform and all associated tools and services, and we are
                committed to taking appropriate steps to protect the information that we collect. While no company can
                guarantee absolute security, we do take reasonable steps to implement appropriate physical, technical
                and organisational measures to protect the personal information that we collect and process.
              </UitkText>

              <UitkSpacing margin={{ blockstart: 'six' }}>
                <UitkHeading tag="h3" size={5}>
                  Record retention
                </UitkHeading>
              </UitkSpacing>
              <UitkText spacing="two">
                We will retain your personal information in accordance with all applicable laws for as long as it may be
                relevant to fulfil the purposes set forth in this Privacy Statement, unless a longer retention period is
                required or permitted by law. We will de-identify, aggregate or otherwise anonymise your personal
                information if we intend to use it for analytical purposes or trend analysis over longer periods of
                time.
              </UitkText>
              <UitkText spacing="two">The criteria that we use to determine our retention periods include:</UitkText>
              <UitkSpacing className="uitk-type-list-400" padding={{ block: 'three', inline: 'four' }}>
                <UitkList>
                  <UitkListItem>
                    The duration of our relationship with you, including any open accounts that you may have with
                    Expedia Group companies, or recent bookings or other transactions that you have made on our platform
                  </UitkListItem>
                  <UitkListItem>
                    Whether we have a legal obligation related to your personal information, such as laws requiring us
                    to keep records of your transactions with us
                  </UitkListItem>
                  <UitkListItem>
                    Whether there are any current and relevant legal obligations affecting how long we will keep your
                    personal information, including contractual obligations, litigation holds, statutes of limitations
                    and regulatory investigations
                  </UitkListItem>
                </UitkList>
              </UitkSpacing>

              <UitkSpacing margin={{ blockstart: 'six' }}>
                <UitkHeading tag="h3" size={5}>
                  Contact Us
                </UitkHeading>
              </UitkSpacing>
              <UitkText spacing="two">
                If you have any questions or concerns about our use of your personal information, please contact us via
                the Privacy section on our Customer service portal here or alternatively, you can email our customer
                service team at <a href={`mailto:${email}`}>{email}</a>. For a list of the Expedia Group companies,
                click{' '}
                <a href="https://www.expediagroup.com/" target="_blank">
                  here
                </a>
                .
              </UitkText>
              <UitkText spacing="two">
                For more information about the data controller and/or EU representative for personal information that we
                process, please click{' '}
                <a
                  href="https://www.expediagroup.com/files/doc_downloads/2021/04/28/Data-Controllers-and-Representatives-globally.pdf"
                  target="_blank"
                >
                  here
                </a>
                .
              </UitkText>

              <UitkSpacing margin={{ blockstart: 'six' }}>
                <UitkHeading tag="h3" size={5}>
                  Updates to Privacy Statement
                </UitkHeading>
              </UitkSpacing>
              <UitkText spacing="two">
                We may update this Privacy Statement in response to changing laws or technical or business developments.
                You can see when this Privacy Statement was last updated by checking the 'last updated' date displayed
                at the top of this Statement.
              </UitkText>
            </div>
          </UitkSpacing>
        </main>
      </UitkSpacing>
    );
  }
}
